import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { inspectSeoTags, type SeoPreviewResult } from "@/lib/seo-preview.functions";
import { SITE_URL, slugify } from "@/content/seo";
import { books } from "@/content/site-content";
import { articles } from "@/content/articles";

const title = "Podgląd tagów SEO | Sylwia Sitkowska";
const description =
  "Narzędzie wewnętrzne: sprawdź canonical, og:url, og:image i twitter:image dla dowolnej podstrony serwisu.";

export const Route = createFileRoute("/seo-preview")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/seo-preview` },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/seo-preview` }],
  }),
  component: SeoPreviewPage,
});

const rows: { label: string; key: keyof SeoPreviewResult }[] = [
  { label: "Status HTTP", key: "status" },
  { label: "Adres końcowy (po przekierowaniach)", key: "finalUrl" },
  { label: "title", key: "title" },
  { label: "meta description", key: "description" },
  { label: "canonical", key: "canonical" },
  { label: "og:url", key: "ogUrl" },
  { label: "og:image", key: "ogImage" },
  { label: "twitter:image", key: "twitterImage" },
  { label: "meta robots", key: "robots" },
];

type GroupId = "home" | "mastermind" | "ksiazki" | "media";

const groups: { id: GroupId; label: string; paths: string[] }[] = [
  { id: "home", label: "Strona główna", paths: ["/", "/o-mnie", "/kontakt", "/wystapienia"] },
  {
    id: "mastermind",
    label: "Oferta",
    paths: ["/mastermind", "/konsultacje", "/rezerwacja"],
  },
  {
    id: "ksiazki",
    label: "Książki",
    paths: [
      "/media-i-publikacje",
      ...books.slice(0, 4).map((book) => `/media-i-publikacje#${slugify(book.title)}`),
    ],
  },
  {
    id: "media",
    label: "Media i teksty",
    paths: ["/media-i-publikacje", "/psychologia-biznesu", ...articles.map((a) => `/${a.slug}`)],
  },
];

function SeoPreviewPage() {
  const inspect = useServerFn(inspectSeoTags);
  const [group, setGroup] = useState<GroupId>("home");
  const [url, setUrl] = useState(`${SITE_URL}/`);
  const [result, setResult] = useState<SeoPreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const activeGroup = groups.find((g) => g.id === group) ?? groups[0]!;

  async function check(target: string) {
    setLoading(true);
    setError(null);
    try {
      setResult(await inspect({ data: { url: target } }));
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : "Nie udało się pobrać strony.");
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    await check(url);
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl px-5 py-16 sm:px-8">
        <h1 className="font-display text-3xl tracking-tight text-ink sm:text-4xl">
          Podgląd tagów SEO
        </h1>
        <p className="mt-3 max-w-prose text-sm leading-relaxed text-ink-soft">
          Wpisz adres podstrony (np. <code>/mastermind</code>), aby zobaczyć aktualne
          znaczniki canonical, og:url, og:image i twitter:image pobrane z serwera. Zbiorcze
          porównanie domen znajdziesz na{" "}
          <Link to="/seo-indeksowanie" className="text-primary underline underline-offset-4">
            /seo-indeksowanie
          </Link>
          .
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <label className="text-sm text-ink-soft" htmlFor="seo-group">
            Typ adresu
          </label>
          <select
            id="seo-group"
            value={group}
            onChange={(e) => setGroup(e.target.value as GroupId)}
            className="rounded-md border border-ink/15 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ink/40"
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {activeGroup.paths.map((path) => (
            <button
              key={path}
              type="button"
              onClick={() => {
                const target = `${SITE_URL}${path}`;
                setUrl(target);
                void check(target);
              }}
              className="rounded-full border border-ink/15 px-3 py-1.5 text-xs text-ink/80 transition-colors hover:border-ink/40 hover:text-ink"
            >
              {path}
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3 sm:flex-row">
          <label className="sr-only" htmlFor="seo-url">
            Adres strony
          </label>
          <input
            id="seo-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://sylwiasitkowska.pl/kontakt"
            className="w-full rounded-md border border-ink/15 bg-white px-4 py-3 text-sm text-ink outline-none focus:border-ink/40"
          />
          <button
            type="submit"
            disabled={loading}
            className="shrink-0 rounded-md bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Sprawdzam…" : "Sprawdź"}
          </button>
        </form>

        {error ? (
          <p className="mt-6 rounded-md border border-ink/15 bg-ink/5 px-4 py-3 text-sm text-ink">
            {error}
          </p>
        ) : null}

        {result ? (
          <div className="mt-10 overflow-hidden rounded-lg border border-ink/10">
            <table className="w-full table-fixed text-left text-sm">
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className="border-b border-ink/10 last:border-0 align-top">
                    <th
                      scope="row"
                      className="w-40 bg-ink/5 px-4 py-3 font-medium text-ink-soft sm:w-56"
                    >
                      {row.label}
                    </th>
                    <td className="break-words px-4 py-3 text-ink">
                      {String(result[row.key] ?? "-")}
                    </td>
                  </tr>
                ))}
                <tr className="border-b border-ink/10 align-top">
                  <th scope="row" className="w-40 bg-ink/5 px-4 py-3 font-medium text-ink-soft sm:w-56">
                    Typy JSON-LD
                  </th>
                  <td className="break-words px-4 py-3 text-ink">
                    {result.jsonLdTypes.length ? result.jsonLdTypes.join(", ") : "-"}
                  </td>
                </tr>
                <tr className="align-top">
                  <th scope="row" className="w-40 bg-ink/5 px-4 py-3 font-medium text-ink-soft sm:w-56">
                    Źródło wyniku
                  </th>
                  <td className="break-words px-4 py-3 text-ink">
                    {result.cachedAt ? `cache (${result.cachedAt})` : "świeże pobranie"}
                  </td>
                </tr>
              </tbody>
            </table>
            {result.ogImage ? (
              <img
                src={result.ogImage}
                alt="Podgląd obrazka Open Graph"
                loading="lazy"
                className="w-full border-t border-ink/10 object-cover"
              />
            ) : null}
          </div>
        ) : null}

        {result ? (
          <section className="mt-10 rounded-lg border border-ink/10 p-5">
            <h2 className="font-display text-xl tracking-tight text-ink">
              Walidacja JSON-LD i obrazka Open Graph
            </h2>

            <dl className="mt-4 grid gap-2 text-sm text-ink/80 sm:grid-cols-2">
              <div>
                <dt className="text-ink-soft">Węzły Book</dt>
                <dd className="text-ink">{result.jsonLd?.bookCount ?? 0}</dd>
              </div>
              <div>
                <dt className="text-ink-soft">Węzły Article</dt>
                <dd className="text-ink">{result.jsonLd?.articleCount ?? 0}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-ink-soft">og:image, status</dt>
                <dd className="text-ink">
                  {result.ogImageCheck
                    ? `${result.ogImageCheck.status} ${result.ogImageCheck.ok ? "OK" : "błąd"}${
                        result.ogImageCheck.width
                          ? ` • ${result.ogImageCheck.width}×${result.ogImageCheck.height} px (${result.ogImageCheck.format})`
                          : ""
                      }${result.ogImageCheck.error ? ` • ${result.ogImageCheck.error}` : ""}`
                    : "brak og:image"}
                </dd>
              </div>
            </dl>

            {result.ogImageCheck && !result.ogImageCheck.meetsRecommended ? (
              <p className="mt-4 rounded-md border border-ink/15 bg-ink/5 px-4 py-3 text-sm text-ink">
                {result.ogImageCheck.meetsMinimum
                  ? "Obrazek spełnia twarde minimum (600×315), ale zalecane jest 1200×630 px."
                  : "Obrazek jest mniejszy niż 600×315 px, platformy zwykle pokażą małą kartę."}
              </p>
            ) : null}

            <ul className="mt-4 space-y-2 text-sm">
              {(result.jsonLd?.issues.length ?? 0) === 0 ? (
                <li className="text-ink-soft">
                  Brak zastrzeżeń: typy Book/Article mają wymagane pola, a og:image jest obecny.
                </li>
              ) : (
                result.jsonLd?.issues.map((issue, index) => (
                  <li
                    key={`${issue.type}-${index}`}
                    className="rounded-md border border-ink/10 px-3 py-2 text-ink/80"
                  >
                    <span
                      className={
                        issue.level === "error"
                          ? "font-medium text-primary"
                          : "font-medium text-ink-soft"
                      }
                    >
                      {issue.level === "error" ? "Błąd" : "Ostrzeżenie"}
                    </span>{" "}
, {issue.message}
                  </li>
                ))
              )}
            </ul>

            {result.jsonLd?.nodes.length ? (
              <p className="mt-4 text-xs text-ink-soft">
                Wykryte węzły:{" "}
                {result.jsonLd.nodes
                  .map((node) => (node.name ? `${node.type} (${node.name})` : node.type))
                  .join(", ")}
              </p>
            ) : null}
          </section>
        ) : null}
      </main>
      <SiteFooter />
    </div>
  );
}
